import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

export function SEOHero({ badge, h1, subtitle, children }) {
  return (
    <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-20 bg-[#0B1120] overflow-hidden">
      <div className="absolute top-0 left-0 w-[700px] h-[700px] bg-blue-600/6 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {badge && (
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-xs font-medium mb-6">
            {badge}
          </span>
        )}
        <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold text-white leading-[1.1] mb-6" style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" }}>
          {h1}
        </h1>
        <p className="text-white/65 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">{subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/einvoice/portal/auth" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold text-sm transition-all">
            Get Started Free <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/5 text-white border border-white/15 hover:bg-white/10 rounded-xl font-semibold text-sm transition-all">
            Book a Demo
          </Link>
        </div>
        {children}
      </div>
    </section>
  );
}

export function SEOSection({ tag, h2, description, children, bg = 'bg-white' }) {
  return (
    <section className={`py-20 lg:py-24 ${bg}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {tag && (
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
            {tag}
          </span>
        )}
        <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" }}>
          {h2}
        </h2>
        {description && <p className="text-slate-600 text-lg max-w-3xl mb-10">{description}</p>}
        {children}
      </div>
    </section>
  );
}

export function FeatureGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item, i) => (
        <div key={i} className="bg-white rounded-2xl p-7 border border-slate-200 hover:shadow-lg transition-all h-full">
          <div className="w-10 h-10 bg-[#2563EB] rounded-lg flex items-center justify-center mb-4">
            <item.icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-2">{item.title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
        </div>
      ))}
    </div>
  );
}

export function BenefitList({ items }) {
  return (
    <div className="space-y-4 max-w-2xl">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-slate-800">{item.title}</p>
            <p className="text-sm text-slate-500">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function InternalLinkGrid({ links }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
      {links.map((link, i) => (
        <Link key={i} to={link.to} className="group p-5 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 transition-all">
          <p className="text-sm font-semibold text-slate-800 group-hover:text-[#2563EB] mb-1">{link.title}</p>
          <p className="text-xs text-slate-400">{link.desc}</p>
        </Link>
      ))}
    </div>
  );
}

export function CTABanner({ title, subtitle }) {
  return (
    <section className="py-16 lg:py-20 bg-[#0B1120]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" }}>{title}</h2>
        <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">{subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/einvoice/portal/auth" className="group px-8 py-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold text-base transition-all flex items-center justify-center gap-2">
            Get Started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/contact" className="px-8 py-4 bg-white/10 text-white rounded-xl font-semibold text-base hover:bg-white/15 transition-all border border-white/20 flex items-center justify-center gap-2">
            Book Consultation
          </Link>
        </div>
      </div>
    </section>
  );
}
